import type { AppShell } from "@/App"
import type { SerialActivationAsk } from "./SerialActivationDialog"

/**
 * Cliente da área Armazém — receção de material com séries (Fase 2).
 * As funções vivem em `functions/api/armazem/**` (guardadas por área).
 */

export type ReceiptStatus =
  | "a_extrair"
  | "a_rever"
  | "pronto"
  | "confirmado"
  | "escrito"
  | "erro"
  | "erro_escrita"
  | "concluida_manual"
  | "duplicado"
  | "descartada"

export type ReceiptOrigin = "foto" | "ficheiro" | "email"

export interface LinkedDelivery {
  origin: ReceiptOrigin
  detail: string | null
  case_id: number | null
  case_number: string | null
  receipt_id?: string | null
  extraction_id?: string | null
  at: string
}

/** «Email de <remetente>», «Foto» ou «Ficheiro». */
export function originLabel(origin: ReceiptOrigin | null | undefined, detail?: string | null): string | null {
  if (origin === "email") return detail ? `Email de ${detail}` : "Email"
  if (origin === "foto") return "Foto"
  if (origin === "ficheiro") return "Ficheiro"
  return null
}

export interface ReceiptListItem {
  id: string
  status: ReceiptStatus
  supplier_name: string | null
  document_number: string | null
  document_date: string | null
  order_names: string[]
  error: string | null
  origin: ReceiptOrigin | null
  origin_detail: string | null
  duplicate_of: string | null
  created_at: string
  line_count: number
  serial_count: number
  /** Fase 2d */
  receipt_code: string | null
  supplier_nif: string | null
  document_type: string | null
  guide_receipt_id: string | null
  total: number | null
  odoo_state: OdooState
  article_count: number
}

export type OdooState = "nada" | "rascunho" | "por_validar" | "escrito" | "erro"

export const ODOO_STATE_LABEL: Record<OdooState, string> = {
  nada: "—",
  rascunho: "Rascunho criado",
  por_validar: "Por validar no armazém",
  escrito: "Escrito",
  erro: "Erro",
}

/** Tipo do documento para o crachá: «Fatura de fornecedor», «Guia», «Fatura de uma guia». */
export function documentTypeLabel(r: { document_type: string | null; guide_receipt_id?: string | null }): string | null {
  if (r.guide_receipt_id) return "Fatura de uma guia"
  const t = (r.document_type ?? "").trim().toUpperCase()
  if (!t) return null
  // «ALBARÁN» (Espanha) e «REMESSA» também são guias — como o `documentKind` do servidor.
  if (/^(GR|GT|GUIA|GD|DELIVERY)/.test(t) || /GUIA|ALBAR|REMESSA|REMISI/.test(t)) return "Guia"
  return "Fatura de fornecedor"
}

export const eurFmt = (v: unknown) =>
  v === null || v === undefined || v === "" ? "—" : Number(v).toLocaleString("pt-PT", { style: "currency", currency: "EUR" })

/** «Conferir caixas» (L3): séries lidas nas caixas e trocas feitas. */
export interface BoxChecks {
  verified?: Record<string, { user: string | null; at: string }>
  corrections?: Array<{ line_index: number; from: string; to: string; user: string | null; at: string }>
}

export interface Receipt {
  id: string
  status: ReceiptStatus
  extraction_status: string | null
  file_keys: string[]
  document_file_key: string | null
  document_mime: string | null
  supplier_name: string | null
  supplier_nif: string | null
  supplier_partner_id: number | null
  supplier_partner_name: string | null
  document_type: string | null
  document_number: string | null
  document_date: string | null
  atcud: string | null
  references_json: { supplier_orders?: string[]; delivery_notes?: string[]; our_orders?: string[] }
  charges_json: Array<{ kind: string; description: string | null; amount: number | null }>
  totals_json: { net?: number | null; vat?: number | null; gross?: number | null }
  warnings_json: string[]
  acknowledged_warnings: Array<string | { code: string; user: string | null; at: string }>
  preflight_json: ReviewIssue[]
  guide_receipt_id: string | null
  /** O fornecedor não saiu do NIF no Odoo: de onde veio (0.38.0; sem `source` = o das encomendas). */
  supplier_confirmation: {
    user: string | null
    at: string
    nif_read: string | null
    partner_id: number
    source?: "encomenda" | "manual" | "associado"
    associated?: boolean
  } | null
  manual_completion: { user: string | null; at: string; note: string; steps_done: string[]; steps_missing: string[] } | null
  /** «Descartar»: quem, quando, o motivo e o caso do inbox fechado (ou porque não fechou). */
  discard_json?: {
    user: string | null
    at: string
    reason: string
    case_closed?: { case_id: number; case_number: string | null } | null
    case_error?: string | null
  } | null
  origin: ReceiptOrigin | null
  origin_detail: string | null
  source_case_number: string | null
  duplicate_of: string | null
  linked_deliveries_json: LinkedDelivery[]
  box_checks_json?: BoxChecks | null
  receipt_code?: string | null
  confidence?: number | string | null
  created_by?: string | null
  updated_at: string
  candidate_orders_json: Array<{ id: number; name: string | null; partner_ref: string | null; matched_by: string; receipt_status?: string | null; score?: number }>
  /** Guia de encomendas já recebidas direto no Odoo, confirmado pela pessoa. */
  received_in_odoo_json?: { order_ids: number[]; user: string | null; at: string } | null
  order_ids: number[]
  order_names: string[]
  receipt_picking_ids: number[]
  vendor_bill_id: number | null
  write_log_json: {
    steps?: string[]
    attachment_error?: string | null
    code_conflicts?: Record<string, string>
    barcodes?: Record<string, string>
    barcode_conflicts?: Record<string, string>
    /** A fatura; desde a 0.43.0 também as datas e as condições com que ficou (host ≥ 0.14.2). */
    bill?: BillLog
    /** «Referência do fornecedor nas encomendas» (0.43.0), por encomenda. */
    partner_refs?: Record<string, PartnerRefEntry>
    /** «Recebido» das linhas Diversos (0.54.0), por linha da encomenda. */
    manual_received?: Record<string, { before: number; target: number; status: "pendente" | "escrito" | "falhou"; message: string | null; at: string }>
    pickings?: Record<string, { picking_id: number; created: number; already_present: number }>
    codes?: Record<string, number>
    serial_rows?: number
    /** Número de série por ativar (0.35.0): a decisão por artigo, com quem e quando. */
    serial_tracking?: Record<string, SerialTrackingDecision>
    /** «Criar encomenda a partir do documento» (0.36.0): cada tentativa, com quem e quando. */
    order_creations?: OrderCreation[]
    /** «Dar entrada no stock» (0.42.0), por encomenda: a receção validada ou porque não. */
    stock?: Record<string, StockEntry>
  }
  error: string | null
  confirmed_at: string | null
  written_at: string | null
  created_at: string
}

/** A fatura em rascunho, como o servidor a regista (0.43.0: datas e condições). */
export interface BillLog {
  id: number
  existing?: boolean
  invoice_date?: string | null
  date?: string | null
  invoice_date_due?: string | null
  payment_term?: { id: number; name: string | null } | null
  payment_terms_applied?: "term" | "due_date" | "partner_default" | null
  accounting_date_message?: string | null
}

/** `2026-12-12` → `12/12/2026`. */
export function ptDate(iso: string | null | undefined): string | null {
  return iso ? iso.slice(0, 10).split("-").reverse().join("/") : null
}

/**
 * Com que condições a fatura ficou (host ≥ 0.14.2): «Condições: 60 Dias»,
 * «Vencimento 12/12/2026 (sem condição igual no Odoo)»; null num host antigo.
 */
export function billTermsText(bill: BillLog | undefined | null): string | null {
  if (!bill) return null
  const due = ptDate(bill.invoice_date_due)
  if (bill.payment_terms_applied === "term") {
    return `Condições: ${bill.payment_term?.name ?? "do Odoo"}${due ? ` · vencimento ${due}` : ""}`
  }
  if (bill.payment_terms_applied === "due_date") return due ? `Vencimento ${due} (sem condição igual no Odoo)` : null
  if (bill.payment_terms_applied === "partner_default") {
    return `Condições do fornecedor no Odoo${bill.payment_term?.name ? `: ${bill.payment_term.name}` : ""}${due ? ` · vencimento ${due}` : ""}`
  }
  return null
}

/** O número do documento na referência do fornecedor de uma encomenda (0.43.0). */
export interface PartnerRefEntry {
  status: "escrita" | "ja_tinha" | "recusada"
  order_name: string | null
  before: string | null
  after: string | null
  message: string | null
  at: string
}

/** Vencimento e condições de pagamento (0.43.0): `mismatch` — o vencimento não bate com os dias. */
export interface PaymentTermsInfo {
  due_date: string | null
  terms: string | null
  days: number | null
  mismatch: boolean
}

/** O passo «Dar entrada no stock» de uma encomenda (0.42.0), como o servidor o regista. */
export interface StockEntry {
  status: "pendente" | "validada" | "nao_validada" | "nada"
  picking_id: number | null
  picking_name: string | null
  already_done?: boolean
  backorder: { id: number; name: string | null } | null
  message: string | null
  partial: boolean
  prepared?: boolean
  articles: number
  units: number
  attempts: number
  at: string
}

/** O que «Dar entrada no stock» vai fazer em cada encomenda (só na revisão). */
export interface StockPreview {
  order_id: number
  order_name: string | null
  picking_name: string | null
  articles: number
  units: number
  partial: boolean
  /** Guia de uma encomenda já recebida direto no Odoo: onde («WH/IN/02879, 26/10/2026»). */
  received_in_odoo?: string
  /** A pessoa confirmou que a guia é dessa entrega. */
  received_confirmed?: boolean
}

export interface OrderCreation {
  outcome: "criada" | "confirmada" | "ligada" | "criada_sem_confirmar" | "confirmacao_falhou" | "aguarda_aprovacao" | "moeda_diferente" | "falhou" | "cancelada"
  order_id: number | null
  order_name: string | null
  partner_ref: string | null
  state: string | null
  by: string | null
  by_name: string | null
  at: string
  lines: number
  prices_entered?: number
  unlinked?: number[]
  message?: string | null
}

/** O que «Criar encomenda a partir do documento» manda ao Odoo, e o que o impede (0.36.0). */
export interface OrderDraft {
  offered: boolean
  blockers: string[]
  partner_id: number | null
  partner_name: string | null
  partner_ref: string | null
  /** Fatura: o preço vem do documento. Guia: da tabela do fornecedor ou do custo do artigo. */
  priced: boolean
  lines: Array<{
    key: string
    line_indexes: number[]
    product_id: number
    product_name: string | null
    default_code: string | null
    quantity: number
    price_unit: number | null
    discount: number | null
    doc: Array<{ line_index: number; description: string | null; supplier_code: string | null }>
  }>
}

/** O plano do diálogo (GET …/purchase-order): preços com a origem, o que existe no Odoo, a impressão digital. */
export interface OrderPlan extends OrderDraft {
  lines: Array<
    OrderDraft["lines"][number] & {
      price_source: "documento" | "tabela" | "custo" | null
      needs_price: boolean
      suggested_price: number | null
      uom_id: number | null
      uom_name: string | null
      purchase_uom_name: string | null
      /** Não entra na encomenda (ex.: oferta, 100 % de desconto): o porquê. */
      excluded: string | null
    }
  >
  action: "create" | "link" | "confirm" | "stop"
  existing: { id: number; name: string | null; state: string | null; partner_ref: string | null } | null
  pending_orders: Array<{ id: number; name: string | null; partner_ref: string | null; products: string[] }>
  fingerprint: string
  /** A moeda da encomenda (ISO, ex.: EUR) e a da empresa. */
  currency: string | null
  company_currency: string | null
  /** Moedas diferentes: fica em rascunho para confirmar no Odoo. */
  currency_hold: boolean
}

/** Artigo do catálogo do Odoo para «Associar» fora das encomendas (0.36.0). */
export interface CatalogProduct {
  id: number
  name: string | null
  default_code: string | null
  barcode: string | null
  tracking: string | null
  supplier_code: string | null
  matched_by: "codigo_fornecedor" | "referencia" | "ean" | "nome"
}

export interface SerialTrackingDecision {
  product_id: number
  product_name: string | null
  serial: string | null
  by: string | null
  by_name: string | null
  at: string
  already: boolean
  decision: "ativado" | "sem_series"
}

/** Artigo com séries nesta receção que o Odoo não rastreia por série, por decidir. */
export interface PendingSerialTracking {
  product_id: number
  product_name: string | null
  tracking: string | null
  line_indexes: number[]
  serials: string[]
}

export interface ReceiptLine {
  id: string
  line_index: number
  supplier_code: string | null
  description: string | null
  quantity: number | string | null
  unit_price: number | string | null
  discount_percent: number | string | null
  discount_chain?: string | null
  net_total: number | string | null
  serials: string[]
  barcode_candidates: string[]
  odoo_product_id: number | null
  odoo_product_name: string | null
  odoo_default_code: string | null
  odoo_tracking: string | null
  odoo_barcode?: string | null
  save_barcode?: boolean | null
  match_method: "codigo" | "sugestao" | "manual" | null
  purchase_order_id: number | null
  purchase_line_id: number | null
  /** «Não é um artigo» (0.41.0): portes ou ignorada; `diversos` (0.54.0) é um artigo lançado como Diversos; null = linha de artigo. */
  line_kind?: LineKind | "diversos" | null
  /** `manual`, `aprendido` (regra do fornecedor) ou `desfeito`. */
  line_kind_source?: "manual" | "aprendido" | "desfeito" | null
}

/** «Não é um artigo…»: encargo de portes, ou fica de fora (serviço, taxa, outro). */
export type LineKind = "charge_portes" | "ignored"

export const LINE_KIND_LABEL: Record<LineKind, string> = {
  charge_portes: "Portes / transporte",
  ignored: "Ignorada",
}

/** A linha está posta de lado (portes, ignorada) — não um Diversos, que é um artigo. */
export function asideKind(l: Pick<ReceiptLine, "line_kind">): LineKind | null {
  return l.line_kind === "charge_portes" || l.line_kind === "ignored" ? l.line_kind : null
}

/** «Lançar como Diversos» (0.54.0). */
export function isMiscLine(l: Pick<ReceiptLine, "line_kind">): boolean {
  return l.line_kind === "diversos"
}

/** «Criar artigo no Odoo» (0.54.0): o que o diálogo manda. */
export interface NewProductInput {
  name: string
  default_code: string | null
  tracking: "none" | "serial"
  categ_id: number | null
  standard_price: number | null
}

/** «Criar artigo no Odoo»: o artigo criado (ou o que já existia com o código deste fornecedor). */
export interface CreatedProduct {
  product_id: number
  default_code: string | null
  display_name: string | null
  existed: boolean
  warnings: string[]
}

/** O valor da linha sem IVA: o total impresso, senão preço × quantidade (com o desconto); null sem preços. */
export function lineAmount(l: Pick<ReceiptLine, "net_total" | "unit_price" | "quantity" | "discount_percent">): number | null {
  const n = (v: unknown) => (v === null || v === undefined || v === "" || !Number.isFinite(Number(v)) ? null : Number(v))
  const total = n(l.net_total)
  if (total !== null) return Math.round(total * 100) / 100
  const unit = n(l.unit_price)
  if (unit === null) return null
  return Math.round(unit * (n(l.quantity) ?? 1) * (1 - (n(l.discount_percent) ?? 0) / 100) * 100) / 100
}

/**
 * Linhas «Portes / transporte» que repetem um encargo do documento (mesmo
 * tipo e valor) — o servidor não as lança duas vezes (`duplicateChargeLines`,
 * espelho). Devolve os `line_index`.
 */
export function duplicateChargeLines(
  documentCharges: ReadonlyArray<{ kind: string; amount: number | null }> | null | undefined,
  lines: ReadonlyArray<Pick<ReceiptLine, "line_index" | "line_kind" | "net_total" | "unit_price" | "quantity" | "discount_percent">>,
): Set<number> {
  const free = (documentCharges ?? []).filter((c) => c.kind === "portes" && (c.amount ?? 0) !== 0).map((c) => Math.round((c.amount as number) * 100))
  const out = new Set<number>()
  for (const l of lines) {
    if (l.line_kind !== "charge_portes") continue
    const amount = lineAmount(l)
    if (amount === null || amount === 0) continue
    const i = free.indexOf(Math.round(amount * 100))
    if (i < 0) continue
    free.splice(i, 1)
    out.add(l.line_index)
  }
  return out
}

export interface ReviewIssue {
  kind: string
  line_index: number | null
  code: string | null
  message: string
}

export interface OrderLine {
  id: number
  order_id: number
  order_name: string
  product_id: number | null
  product_name: string | null
  default_code: string | null
  supplier_code: string | null
  product_qty: number
  qty_to_receive: number
  tracking: string | null
}

export interface ConfirmStepInfo {
  key: string
  label: string
  done: boolean
}

export interface ReviewNotice {
  kind: string
  line_index: number
  message: string
}

export interface LineCandidate {
  purchase_line_id: number
  order_id: number
  product_id: number | null
  product_name: string | null
  default_code: string | null
  qty_to_receive: number
  percent: number
}

export interface ReceiptDetail {
  receipt: Receipt
  lines: ReceiptLine[]
  /** IVA e ecotaxa por linha, como foram lidos (Fase 2d). */
  doc_lines?: Array<{ line_index: number; vat_rate: number | null; eco_fee: number | null }>
  /** «Parecido com»: linhas de encomenda parecidas, por linha por casar ou sugerida. */
  line_candidates?: Record<string, LineCandidate[]>
  /** Artigo Odoo de cada encargo (Configurações). */
  charge_products?: Record<string, string | null>
  /** Linhas por casar que parecem portes (pela descrição): só uma sugestão (0.41.0). */
  line_kind_suggestions?: Record<string, LineKind>
  /** O artigo Diversos configurado (0.54.0); null = sem «Lançar como Diversos». */
  misc_code?: string | null
  /** Fatura ou guia, como o servidor a lê: numa guia os portes não vão a lado nenhum. */
  document_kind?: "invoice" | "guide" | "other" | "unknown"
  /** Só na resposta de «Não é um artigo…»/«desfazer»: se a regra do fornecedor ficou guardada (null sem fornecedor). */
  line_kind_rule?: { saved: boolean; error: string | null } | null
  issues: ReviewIssue[]
  /** Avisos que não bloqueiam (ex.: «O» numa série). */
  notices: ReviewNotice[]
  /** Guias já tratadas que esta fatura pode fechar (a pessoa escolhe). */
  guide_candidates: Array<{ id: string; document_number: string | null; document_date: string | null; order_names: string[]; serial_count: number }>
  /** Fornecedor das encomendas, para «É este o fornecedor» (NIF lido sem parceiro). */
  order_supplier: { id: number; name: string | null; vat: string | null } | null
  /** Só na resposta de «Escolher fornecedor» com «Associar este NIF»: se a associação ficou guardada. */
  supplier_alias?: { saved: boolean; error: string | null } | null
  manual_completion_by: string | null
  /** Documento repetido: a receção que já o tinha. */
  duplicate: { id: string; status: ReceiptStatus; origin: ReceiptOrigin | null; created_at: string } | null
  steps: ConfirmStepInfo[]
  /** «Dar entrada no stock» (0.42.0): por encomenda, a receção, artigos e se fica entrega pendente. */
  stock_preview?: StockPreview[]
  /** Vencimento e condições (0.43.0): os da receção, ou os da leitura numa receção antiga. */
  payment_terms?: PaymentTermsInfo
  /** «Referência do fornecedor nas encomendas» (0.43.0): o que fica em cada uma. */
  partner_ref_preview?: Array<{ order_id: number; order_name: string | null; before: string | null; after: string | null; changes: boolean }>
  order_lines: OrderLine[]
  /** As linhas das encomendas não vieram todas do Odoo (ocupado, em baixo): mostrar o aviso. */
  odoo_warning?: string | null
  /** Artigos sem número de série no Odoo com séries nesta receção, por decidir (0.35.0). */
  pending_serial_tracking?: PendingSerialTracking[]
  /** «Criar encomenda a partir do documento» (0.36.0); `null` fora da revisão. */
  order_draft?: OrderDraft | null
  /** Lidos como «nossa encomenda» sem serem nossos (`P0…`): a encomenda do fornecedor. */
  supplier_order_refs?: string[]
}

export interface ConfirmProgress {
  receipt: Receipt
  step: string | null
  steps: ConfirmStepInfo[]
  done: boolean
}

type Res<T> = { success: true; data: T } | { success: false; error: string }

function unwrap<T>(res: Res<T>): T {
  if (!res.success) throw new Error(res.error || "Pedido falhou")
  return res.data
}

export async function listReceipts(shell: AppShell): Promise<ReceiptListItem[]> {
  return unwrap(await shell.client.get<Res<{ items: ReceiptListItem[] }>>("armazem/receipts")).items
}

export async function getReceipt(shell: AppShell, id: string): Promise<ReceiptDetail> {
  return unwrap(await shell.client.get<Res<ReceiptDetail>>(`armazem/receipts/${id}`))
}

export async function patchReceipt(shell: AppShell, id: string, patch: Record<string, unknown>): Promise<ReceiptDetail> {
  return unwrap(await shell.client.patch<Res<ReceiptDetail>>(`armazem/receipts/${id}`, patch))
}

/** O aviso «ativar o número de série» de um artigo desta receção (só leitura). */
export async function getSerialActivation(shell: AppShell, id: string, productId: number): Promise<SerialActivationAsk> {
  return unwrap(await shell.client.get<Res<SerialActivationAsk>>(`armazem/receipts/${id}/serial-activation?product_id=${productId}`))
}

/** As categorias de artigos do Odoo (diálogo «Criar artigo no Odoo», 0.54.0). */
export async function productCategories(shell: AppShell, id: string): Promise<Array<{ id: number; name: string }>> {
  return unwrap(await shell.client.get<Res<{ categories: Array<{ id: number; name: string }> }>>(`armazem/receipts/${id}/products?categories=1`)).categories
}

/** «Criar artigo no Odoo» numa linha (0.54.0): cria-o com stock e associa a linha. */
export async function createLineProduct(shell: AppShell, id: string, lineIndex: number, input: NewProductInput): Promise<CreatedProduct> {
  return unwrap(await shell.client.post<Res<{ product: CreatedProduct }>>(`armazem/receipts/${id}/products`, { line_index: lineIndex, ...input })).product
}

/** «Associar»: artigos do catálogo do Odoo (referência, nome, EAN, código do fornecedor). */
export async function searchCatalog(shell: AppShell, id: string, q: string): Promise<CatalogProduct[]> {
  return unwrap(await shell.client.get<Res<{ products: CatalogProduct[] }>>(`armazem/receipts/${id}/products?q=${encodeURIComponent(q)}`)).products
}

/**
 * O EAN do documento que vai para o artigo ao confirmar — a mesma regra do
 * servidor (`barcodeToSave`): artigo confirmado, sem código de barras no Odoo,
 * e a pessoa não o tirou.
 */
export function barcodeToSave(l: Pick<ReceiptLine, "barcode_candidates" | "odoo_product_id" | "match_method" | "odoo_barcode" | "save_barcode">): string | null {
  const code = (l.barcode_candidates ?? [])[0]
  if (!code || !l.odoo_product_id) return null
  if (l.match_method !== "codigo" && l.match_method !== "manual") return null
  if (l.odoo_barcode) return null
  return l.save_barcode === false ? null : code
}

/** Um parceiro do Odoo para «Escolher fornecedor» (0.38.0). */
export interface SupplierOption {
  id: number
  name: string | null
  vat: string | null
  supplier_rank: number
  is_company: boolean
  city: string | null
  country: string | null
}

/** Parceiros do Odoo pelo nome ou NIF — fornecedores e empresas primeiro (só leitura). */
export async function searchSuppliers(shell: AppShell, id: string, q: string): Promise<SupplierOption[]> {
  return unwrap(await shell.client.get<Res<{ suppliers: SupplierOption[] }>>(`armazem/receipts/${id}/suppliers?q=${encodeURIComponent(q)}`)).suppliers
}

/** De onde veio o fornecedor da receção, para o cartão FORNECEDOR. */
export function supplierSource(r: Pick<Receipt, "supplier_partner_id" | "supplier_confirmation" | "supplier_nif">): string | null {
  if (!r.supplier_partner_id) return null
  const c = r.supplier_confirmation
  if (c?.source === "manual") return c.associated && c.nif_read ? `escolhido à mão · NIF ${c.nif_read} associado` : "escolhido à mão"
  if (c?.source === "associado") return `associado (NIF ${c.nif_read ?? r.supplier_nif ?? "?"})`
  if (c) return "confirmado pela encomenda"
  return r.supplier_nif ? "pelo NIF" : "pela encomenda"
}

/** O plano do diálogo «Criar encomenda a partir do documento» (só leitura no Odoo). */
export async function getOrderPlan(shell: AppShell, id: string): Promise<OrderPlan> {
  return unwrap(await shell.client.get<Res<OrderPlan>>(`armazem/receipts/${id}/purchase-order`))
}

export interface CreatedOrder {
  order: { order_id: number; name: string | null; created: boolean; confirmed_now: boolean }
  unlinked: number[]
  /** Acima do limite da dupla validação: em rascunho, à espera de aprovação no Odoo. */
  awaiting_approval: { amount_total: number; limit: number } | null
  /** Moeda do fornecedor ≠ a da empresa: em rascunho, a confirmar no Odoo. */
  currency_hold: { order_currency: string | null; company_currency: string | null } | null
}

/** «Criar e confirmar no Odoo»: com a impressão digital do plano mostrado e os preços indicados (por `key`). */
export async function createPurchaseOrder(shell: AppShell, id: string, body: { fingerprint: string; prices?: Record<string, number> }): Promise<CreatedOrder> {
  return unwrap(await shell.client.post<Res<CreatedOrder>>(`armazem/receipts/${id}/purchase-order`, body))
}

/** Um passo da confirmação (o ecrã repete até `done`); `retryStock` — «Validar outra vez» numa receção escrita. */
export async function confirmStep(shell: AppShell, id: string, opts: { retryStock?: boolean } = {}): Promise<ConfirmProgress> {
  return unwrap(await shell.client.post<Res<ConfirmProgress>>(`armazem/receipts/${id}/confirm`, opts.retryStock ? { retry_stock: true } : {}))
}

/** As encomendas cuja receção o Odoo não validou (a receção está escrita, com o aviso). */
export function pendingStock(receipt: Pick<Receipt, "write_log_json">): StockEntry[] {
  return Object.values(receipt.write_log_json?.stock ?? {}).filter((e) => e.status === "nao_validada")
}

/**
 * Sobe um ficheiro direto ao host (rota `files` da app, multipart). O proxy
 * REST das funções não passa binários — por isso as funções só recebem chaves.
 */
export async function uploadFile(shell: AppShell, file: File): Promise<string> {
  const form = new FormData()
  form.append("file", file, file.name || "foto.jpg")
  const res = await shell.client.post<Res<{ key: string }>>("files", undefined, { body: form })
  return unwrap(res).key
}

export async function createReceipt(shell: AppShell, fileKeys: string[], origin: "foto" | "ficheiro" = "ficheiro"): Promise<Receipt> {
  return unwrap(await shell.client.post<Res<Receipt>>("armazem/receipts", { file_keys: fileKeys, origin }))
}

/** URL temporário do documento de uma receção (também o de um email, que é do caso do inbox). */
export async function receiptDocumentUrl(shell: AppShell, id: string): Promise<string> {
  return unwrap(await shell.client.get<Res<{ url: string }>>(`armazem/receipts/${id}/document`)).url
}

/** URL temporário (5 min) para ver um ficheiro da app. */
export async function fileUrl(shell: AppShell, key: string): Promise<string> {
  return unwrap(await shell.client.get<Res<{ url: string }>>(`files?key=${encodeURIComponent(key)}`)).url
}

export async function getChargeProducts(shell: AppShell): Promise<Record<string, string | null>> {
  return unwrap(await shell.client.get<Res<{ receipt_charge_products: Record<string, string | null> }>>("armazem/settings"))
    .receipt_charge_products
}

export async function saveChargeProducts(shell: AppShell, value: Record<string, string | null>): Promise<void> {
  unwrap(await shell.client.patch<Res<unknown>>("armazem/settings", { receipt_charge_products: value }))
}

/** Rondas de stock: as equipas dos responsáveis do armazém (fazem rondas «Tudo da localização»). */
export interface RondaConfig {
  responsible_teams: string[]
}

export async function getRondaConfig(shell: AppShell): Promise<RondaConfig> {
  const got = unwrap(await shell.client.get<Res<{ ronda_config?: RondaConfig }>>("armazem/settings")).ronda_config
  return got ?? { responsible_teams: [] }
}

export async function saveRondaConfig(shell: AppShell, value: RondaConfig): Promise<RondaConfig> {
  return unwrap(await shell.client.patch<Res<{ ronda_config: RondaConfig }>>("armazem/settings", { ronda_config: value })).ronda_config
}

/** «Descartar» uma receção criada por engano (nada escrito no Odoo), com o motivo. */
export async function discardReceipt(shell: AppShell, id: string, reason: string): Promise<void> {
  unwrap(await shell.client.post<Res<unknown>>(`armazem/receipts/${id}/discard`, { reason }))
}

export const STATUS_LABEL: Record<ReceiptStatus, string> = {
  a_extrair: "A ler o documento",
  a_rever: "A rever",
  pronto: "Pronto a confirmar",
  confirmado: "A escrever no Odoo",
  escrito: "Escrito no Odoo",
  erro: "Erro",
  erro_escrita: "Parado a meio da escrita",
  concluida_manual: "Concluída à mão",
  duplicado: "Documento repetido",
  descartada: "Descartada",
}

export const STATUS_VARIANT: Record<ReceiptStatus, "info" | "warning" | "success" | "neutral" | "destructive"> = {
  a_extrair: "info",
  a_rever: "warning",
  pronto: "success",
  confirmado: "info",
  escrito: "neutral",
  erro: "destructive",
  erro_escrita: "destructive",
  concluida_manual: "neutral",
  duplicado: "neutral",
  descartada: "neutral",
}

/**
 * Conferir uma caixa (correção 10): sem a tranca da receção; devolve só as
 * caixas conferidas e o contador.
 */
export async function checkBox(
  shell: AppShell,
  id: string,
  serial: string,
): Promise<{ box_checks_json: BoxChecks; box_done: number; box_total: number }> {
  return unwrap(await shell.client.patch<Res<{ box_checks_json: BoxChecks; box_done: number; box_total: number }>>(`armazem/receipts/${id}`, { box_check: serial }))
}

/** «Dar por concluída» (só admins): não escreve nada no Odoo. */
export async function completeReceipt(shell: AppShell, id: string, note: string): Promise<Receipt> {
  return unwrap(await shell.client.post<Res<{ receipt: Receipt }>>(`armazem/receipts/${id}/complete`, { note })).receipt
}
